import { getSupabase } from '@/lib/supabase';

export async function debugGoalsTrackersSetup() {
  const sb = getSupabase();
  if (!sb) {
    return { error: 'Supabase not configured' };
  }

  try {
    // Check authentication
    const {
      data: { user },
      error: authError,
    } = await sb.auth.getUser();
    if (authError || !user) {
      return { error: 'Not authenticated', authError };
    }

    // Check if tables exist
    const { data: goalsTables, error: goalsError } = await sb
      .from('client_goals')
      .select('count', { count: 'exact', head: true });

    const { data: trackersTable, error: trackersError } = await sb
      .from('client_trackers')
      .select('count', { count: 'exact', head: true });

    // Test basic read permissions
    const { data: _goalsData, error: goalsReadError } = await sb.from('client_goals').select('*').limit(1);

    const { data: _trackersData, error: trackersReadError } = await sb.from('client_trackers').select('*').limit(1);

    return {
      userId: user.id,
      userEmail: user.email,
      tables: {
        client_goals: {
          exists: !goalsError,
          error: goalsError?.message,
          canRead: !goalsReadError,
          readError: goalsReadError?.message,
          count: (goalsTables as any)?.[0]?.count || 0,
        },
        client_trackers: {
          exists: !trackersError,
          error: trackersError?.message,
          canRead: !trackersReadError,
          readError: trackersReadError?.message,
          count: (trackersTable as any)?.[0]?.count || 0,
        },
      },
      timestamp: new Date().toISOString(),
    };
  } catch (error: any) {
    return { error: error.message };
  }
}

// Test goal creation with minimal data
export async function testGoalCreation(clientId: string) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const {
    data: { user },
    error: authError,
  } = await sb.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  const testGoal = {
    client_id: clientId,
    owner_coach_id: user.id,
    title: 'Test Goal',
    type: 'custom',
    unit: 'points',
    target_value: 100,
    current_value: 0,
    priority: 'medium',
    share_with_coach: true,
  };

  console.log('🧪 Testing goal creation:', testGoal);

  const { data, error } = await sb.from('client_goals').insert(testGoal).select('*').single();

  if (error) {
    console.error('❌ Test goal creation failed:', error);
    throw error;
  }

  console.log('✅ Test goal created:', data);
  return data;
}

// Test tracker creation with minimal data
export async function testTrackerCreation(clientId: string) {
  const sb = getSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const {
    data: { user },
    error: authError,
  } = await sb.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  const testTracker = {
    client_id: clientId,
    owner_coach_id: user.id,
    title: 'Test Tracker',
    unit: 'lbs',
    target_direction: 'down',
    schedule: { cadence: 'weekly', daysOfWeek: [0], remindersEnabled: true },
    show_in_analytics: true,
    is_archived: false,
  };

  console.log('🧪 Testing tracker creation:', testTracker);

  const { data, error } = await sb.from('client_trackers').insert(testTracker).select('*').single();

  if (error) {
    console.error('❌ Test tracker creation failed:', error);
    throw error;
  }

  console.log('✅ Test tracker created:', data);
  return data;
}

// Test which adapters are being used
export async function debugAdapterSelection() {
  try {
    const { goalsStore } = await import('@/data/storage/goals/adapter');
    const { trackersStore } = await import('@/data/storage/trackers/adapter');
    const { isSupabaseConfigured, getSupabase } = await import('@/lib/supabase');

    return {
      isSupabaseConfigured: isSupabaseConfigured(),
      hasSupabaseClient: !!getSupabase(),
      goalsStoreType:
        goalsStore === (await import('@/data/storage/goals/supabase')).supabaseGoalsStore ? 'supabase' : 'localStorage',
      trackersStoreType:
        trackersStore === (await import('@/data/storage/trackers/supabase')).supabaseTrackersStore
          ? 'supabase'
          : 'localStorage',
      timestamp: new Date().toISOString(),
    };
  } catch (error: any) {
    return { error: error.message };
  }
}

// Add to window for console testing
if (typeof window !== 'undefined') {
  (window as any).debugGoalsTrackers = {
    debugSetup: debugGoalsTrackersSetup,
    testGoal: testGoalCreation,
    testTracker: testTrackerCreation,
    debugAdapters: debugAdapterSelection,
  };
}
